var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState, useMemo } from 'react';
import { useCurrentAccount } from '@mysten/dapp-kit';
import { useBalances } from '../context/BalanceContext';
import { useNetworkString } from '../context/NetworkContext';
import { getSuiClient } from '../utils/client';
const BalanceTestPage = () => {
    const { walletBalancesMap, isLoading } = useBalances();
    const currentAccount = useCurrentAccount();
    const network = useNetworkString();
    const [metaMap, setMetaMap] = useState({});
    const [search, setSearch] = useState('');
    // Stable sorted order for tokens
    const sortedCoinTypes = useMemo(() => {
        if (!walletBalancesMap)
            return [];
        return Array.from(walletBalancesMap.keys()).sort();
    }, [walletBalancesMap]);
    // Fetch metadata for each coinType and cache them
    useEffect(() => {
        if (!walletBalancesMap || walletBalancesMap.size === 0)
            return;
        const suiClient = getSuiClient(network);
        const missingCoinTypes = sortedCoinTypes.filter((coinType) => !(coinType in metaMap));
        if (missingCoinTypes.length === 0)
            return;
        let cancelled = false;
        (() => __awaiter(void 0, void 0, void 0, function* () {
            const updates = {};
            for (const coinType of missingCoinTypes) {
                try {
                    const meta = yield suiClient.getCoinMetadata({ coinType });
                    if (!meta) {
                        updates[coinType] = { decimals: null, name: null, iconUrl: null };
                    }
                    else {
                        updates[coinType] = {
                            decimals: typeof meta.decimals === 'number' ? meta.decimals : null,
                            name: meta.name || null,
                            iconUrl: meta.iconUrl || null,
                        };
                    }
                }
                catch (err) {
                    console.log(`❌ [Metadata] Failed to fetch for ${coinType}:`, err);
                    updates[coinType] = { decimals: null, name: null, iconUrl: null };
                }
            }
            if (!cancelled) {
                setMetaMap((prev) => (Object.assign(Object.assign({}, prev), updates)));
            }
        }))();
        return () => {
            cancelled = true;
        };
    }, [walletBalancesMap, network, metaMap, sortedCoinTypes]);
    // Helper: are all metas loaded?
    const allMetaLoaded = useMemo(() => {
        if (!walletBalancesMap || sortedCoinTypes.length === 0)
            return false;
        return sortedCoinTypes.every((coinType) => {
            const meta = metaMap[coinType];
            return meta && typeof meta.decimals === 'number' && meta.name !== null;
        });
    }, [walletBalancesMap, metaMap, sortedCoinTypes]);
    // Filtered tokens by search
    const filteredCoinTypes = useMemo(() => {
        if (!search)
            return sortedCoinTypes;
        const lower = search.toLowerCase();
        return sortedCoinTypes.filter((coinType) => {
            const meta = metaMap[coinType];
            return (coinType.toLowerCase().includes(lower) ||
                ((meta === null || meta === void 0 ? void 0 : meta.name) && meta.name.toLowerCase().includes(lower)));
        });
    }, [search, sortedCoinTypes, metaMap]);
    return (_jsx("div", { className: "container mx-auto px-4 py-8", children: _jsxs("div", { className: "bg-white rounded-lg shadow-md p-6", children: [_jsx("h1", { className: "text-2xl font-bold mb-4", children: "Balance Test Page" }), _jsxs("div", { className: "space-y-4", children: [_jsxs("div", { children: [_jsx("strong", { children: "Network:" }), " ", network] }), _jsxs("div", { children: [_jsx("strong", { children: "Wallet:" }), " ", (currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.address) || 'Not connected'] }), _jsxs("div", { children: [_jsx("strong", { children: "Loading:" }), " ", isLoading ? 'Yes' : 'No'] }), _jsxs("div", { children: [_jsx("strong", { children: "Tokens Found:" }), " ", (walletBalancesMap === null || walletBalancesMap === void 0 ? void 0 : walletBalancesMap.size) || 0] }), walletBalancesMap && walletBalancesMap.size > 0 && (_jsxs("div", { children: [_jsx("strong", { children: "Balances:" }), _jsx("div", { className: "mb-2", children: _jsx("input", { type: "text", className: "border rounded px-2 py-1 w-full max-w-md", placeholder: "Search by name or coin type...", value: search, onChange: (e) => setSearch(e.target.value) }) }), !allMetaLoaded ? (_jsx("div", { className: "py-4 text-blue-500 font-semibold", children: "Loading token metadata..." })) : (_jsx("ul", { className: "mt-2 space-y-1", children: filteredCoinTypes.map((coinType) => {
                                        const balance = walletBalancesMap.get(coinType);
                                        const meta = metaMap[coinType];
                                        let display;
                                        if (meta && typeof meta.decimals === 'number' && !isNaN(meta.decimals)) {
                                            const displayBalance = (parseInt(balance) / Math.pow(10, meta.decimals)).toFixed(meta.decimals);
                                            display = (_jsxs("div", { children: ["Balance: ", displayBalance, " (raw: ", balance, ", decimals: ", meta.decimals, ")"] }));
                                        }
                                        else {
                                            display = null;
                                        }
                                        return (_jsxs("li", { className: "flex items-center gap-3 text-sm bg-gray-100 p-2 rounded", children: [(meta === null || meta === void 0 ? void 0 : meta.iconUrl) ? (_jsx("img", { src: meta.iconUrl, alt: meta.name || coinType, className: "w-6 h-6 rounded-full border border-gray-300" })) : (_jsx("div", { className: "w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center text-xs text-gray-600", children: "?" })), _jsxs("div", { className: "flex-1", children: [_jsx("div", { className: "font-mono break-all", children: coinType }), _jsx("div", { className: "font-semibold", children: (meta === null || meta === void 0 ? void 0 : meta.name) || '' }), display] })] }, coinType));
                                    }) }))] }))] })] }) }));
};
export default BalanceTestPage;
